import { ChangeDetectionStrategy, Component, computed, effect, inject, input } from '@angular/core';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';

import { CooperationActionsService } from '@app/shared/services/actions/cooperation-actions.service';
import { ActiveCompanyService } from '@app/shared/services/active-company.service';
import { CooperationsDataService } from '@app/shared/services/cooperations-data.service';
import { CooperationStatusEnum } from '@app/shared/services/dtos/cooperation/cooperation-status.enum';
import { GroupsDataService } from '@app/shared/services/groups-data.service';
import { DialogService } from '@app/shared/services/shared/dialog.service';
import { Group } from '@app/shared/types/models/group/group.model';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'proffeo-cooperation-groups-list',
  standalone: true,
  imports: [TranslatePipe, UiButtonComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './cooperation-groups-list.component.html'
})
export class CooperationGroupsListComponent {
  private readonly activeCompanyService = inject(ActiveCompanyService);
  private readonly cooperationsDataService = inject(CooperationsDataService);
  private readonly cooperationsActionsService = inject(CooperationActionsService);
  private readonly dialogService = inject(DialogService);
  private readonly groupsDataService = inject(GroupsDataService);
  private readonly translateService = inject(TranslateService);
  private selectedGroupsForModal = computed(() => {
    const list = this.cooperationGroups() ?? [];
    return list.map(cg => cg.group()).filter((g): g is Group => !!g);
  });

  protected cooperationGroups = computed(() =>
    this.cooperationId() ? this.cooperationsDataService.getCooperationGroups(this.cooperationId()!)() : undefined
  );

  protected companyId = this.activeCompanyService.activeCompanyId;

  protected canEditGroups = computed(() => {
    const id = this.cooperationId();
    if (!id) return false;
    const c = this.cooperationsDataService.getById(id)();
    const status = c?.status;
    return status === CooperationStatusEnum.Pending || status === CooperationStatusEnum.Accepted;
  });

  protected currentGroupIds = computed(() => {
    const list = this.cooperationGroups() ?? [];
    return list.map(cg => cg.group()?.id).filter((id): id is string => !!id);
  });

  public readonly cooperationId = input<string | undefined>(undefined);
  public readonly writable = input<boolean>(false);

  public constructor() {
    effect(() => {
      const id = this.cooperationId();
      if (id) this.cooperationsDataService.loadCooperationGroups(id);
    });

    effect(() => {
      if (!this.writable()) return;
      const cid = this.companyId();
      if (cid) this.groupsDataService.loadCompanyGroups(cid);
    });
  }

  protected openAddGroupsDialog(): void {
    const coopId = this.cooperationId();
    if (!coopId || !this.canEditGroups()) return;

    const ref = this.dialogService.openSelectGroupsDialog({
      groups: this.groupsDataService.getCompanyGroups(this.companyId()!),
      lockedGroups: [],
      selectedGroups: this.selectedGroupsForModal()
    });
    ref.afterClosed().subscribe((selectedGroups: Group[] | undefined) => {
      if (selectedGroups?.length) {
        this.cooperationsActionsService
          .setCooperationGroups(
            coopId,
            selectedGroups.map(g => g.id)
          )
          .subscribe();
      }
    });
  }

  protected removeGroup(groupId: string): void {
    const coopId = this.cooperationId();
    if (!coopId || !groupId || !this.canEditGroups()) return;

    const ref = this.dialogService.openConfirmationDialog({
      title: this.translateService.instant('cooperations.remove-group-confirmation-title'),
      desc: this.translateService.instant('cooperations.remove-group-confirmation-desc')
    });
    ref.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        const nextIds = this.currentGroupIds().filter(id => id !== groupId);
        this.cooperationsActionsService.setCooperationGroups(coopId, nextIds).subscribe();
      }
    });
  }
}
